import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  styled,
  Switch,
  Typography,
} from "@mui/material";
import { useTransaction } from "../../../../hooks/useTransaction";
import { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Sell } from "@mui/icons-material";
import { useProduct } from "../../../../hooks/useProduct";

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#fff" d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4"/></svg>`,
        )}")`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#aab4be",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "60%",
      height: "60%",
      left: "20%",
      top: "20%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#fff" d="m21.41 11.41-8.83-8.83c-.37-.37-.88-.58-1.41-.58H4c-1.1 0-2 .9-2 2v7.17c0 .53.21 1.04.59 1.41l8.83 8.83c.78.78 2.05.78 2.83 0l7.17-7.17c.78-.78.78-2.04-.01-2.83M6.5 8C5.67 8 5 7.33 5 6.5S5.67 5 6.5 5 8 5.67 8 6.5 7.33 8 6.5 8"/></svg>`,
      )}")`,
    },
    ...theme.applyStyles("dark", {
      backgroundColor: "#003892",
    }),
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: "#aab4be",
    borderRadius: 20 / 2,
  },
}));

const TabDashboard = () => {
  const { transactions } = useTransaction();
  const { products } = useProduct();
  const totalIncome = transactions.reduce((total, transaction) => {
    return total + transaction.total;
  }, 0);

  const sales = useMemo(() => {
    const salesData: any = {};
    transactions.forEach((transaction) => {
      transaction.items.forEach((item) => {
        if (salesData[item.id]) {
          salesData[item.id] += item.number;
        } else {
          salesData[item.id] = item.number;
        }
      });
    });
    return salesData;
  }, [transactions]);

  const [chartType, setChartType] = useState<"sell" | "money">("sell");

  const option = useMemo(() => {
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          data: Object.keys(sales).map((id) => {
            const product = transactions
              .flatMap((transaction) => transaction.items)
              .find((item) => item.id === id);
            return product?.name || "";
          }),
          axisTick: {
            alignWithLabel: true,
          },
        },
      ],
      yAxis: [
        {
          type: "value",
        },
      ],
      series: [
        {
          name: chartType === "sell" ? "銷量" : "收入",
          type: "bar",
          barWidth: "60%",
          data:
            chartType === "sell"
              ? Object.values(sales)
              : Object.keys(sales).map((key) => {
                  return (
                    (products.find((product) => product.id === key)?.price ||
                      0) * sales[key]
                  );
                }),
        },
      ],
    };
  }, [chartType]);
  return (
    <>
      <Box
        mb={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" gutterBottom>
          儀表板
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid size={4}>
          <Paper sx={{ p: 2, color: "#fff", bgcolor: "primary.main" }}>
            <Typography variant="subtitle1" gutterBottom>
              總收入
            </Typography>
            <Typography variant="h4" fontWeight={500}>
              $ {totalIncome}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Box display="flex" alignItems={"center"}>
                <Box height={20} borderRight={3} borderColor={"primary.main"} />
                <Typography variant="subtitle1" ml={1}>
                  商品銷售數量
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                <Typography>數量</Typography>
                <MaterialUISwitch
                  sx={{ m: 1 }}
                  checked={chartType === "money"}
                  onChange={(e) =>
                    setChartType(e.target.checked ? "money" : "sell")
                  }
                />
                <Typography>金額</Typography>
              </Stack>
            </Stack>
            <ReactECharts option={option} />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default TabDashboard;
